import React, { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Header } from "../components/Header";
import { Headphones, FileText, Users, Trash2, Loader2, Plus, Check, UserPlus, Mail, Lock, Globe, Reply, Eye, Bell, Send } from "lucide-react";
import { motion } from "framer-motion";

const API = `/api`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('audio');
  const [users, setUsers] = useState([]);
  const [audios, setAudios] = useState([]);
  const [tips, setTips] = useState([]);
  const [messages, setMessages] = useState([]);
  const [adminNotifs, setAdminNotifs] = useState([]);
  const [newNotif, setNewNotif] = useState({ title: '', message: '' });
  const [sendingNotif, setSendingNotif] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newTip, setNewTip] = useState({ title: '', content: '' });
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: '' });
  const [audioAccess, setAudioAccess] = useState({});
  const fileRef = useRef(null);
  const [toast, setToast] = useState(null);

  const fetchAll = useCallback(() => {
    axios.get(`${API}/admin/users`).then(r => setUsers(r.data)).catch(() => {});
    axios.get(`${API}/admin/audio`).then(r => setAudios(r.data)).catch(() => {});
    axios.get(`${API}/tips`).then(r => setTips(r.data)).catch(() => {});
    axios.get(`${API}/admin/audio-access`).then(r => setAudioAccess(r.data)).catch(() => {});
    axios.get(`${API}/admin/messages`).then(r => setMessages(r.data)).catch(() => {});
    axios.get(`${API}/admin/notifications`).then(r => setAdminNotifs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user?.role === 'admin') fetchAll(); }, [user, fetchAll]);

  if (user?.role !== 'admin') return <Navigate to="/dashboard" />;

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const handleUploadAudio = async (e) => {
    e.preventDefault();
    if (!fileRef.current?.files[0]) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', fileRef.current.files[0]);
    formData.append('title', uploadForm.title);
    formData.append('description', uploadForm.description);
    formData.append('category', uploadForm.category);
    try {
      await axios.post(`${API}/admin/audio/upload`, formData);
      showToast('Audio uploadé avec succès');
      setUploadForm({ title: '', description: '', category: '' });
      fileRef.current.value = '';
      fetchAll();
    } catch (err) { showToast(err.response?.data?.detail || 'Erreur', 'error'); }
    finally { setUploading(false); }
  };

  const handleGrantAll = async (audioId) => {
    try {
      const { data } = await axios.post(`${API}/admin/audio/${audioId}/grant-all`);
      if (data.count === 0) {
        showToast('Aucun client inscrit pour le moment. Les audios seront accessibles dès leur inscription.', 'info');
      } else {
        showToast(`Accès accordé à ${data.count} client${data.count > 1 ? 's' : ''}`);
      }
      fetchAll();
    } catch (err) { showToast(err.response?.data?.detail || 'Erreur', 'error'); }
  };

  const handleGrantUser = async (audioId, userId) => {
    await axios.post(`${API}/admin/audio/${audioId}/grant/${userId}`);
    showToast('Accès accordé');
  };

  const handleDeleteAudio = async (id) => {
    await axios.delete(`${API}/admin/audio/${id}`);
    fetchAll();
  };

  const handleTogglePrivate = async (id) => {
    await axios.post(`${API}/admin/audio/${id}/toggle-private`);
    fetchAll();
  };

  const handleCreateTip = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/tips`, newTip);
    setNewTip({ title: '', content: '' });
    showToast('Conseil publié');
    fetchAll();
  };

  const handleDeleteTip = async (id) => {
    await axios.delete(`${API}/admin/tips/${id}`);
    fetchAll();
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      showToast('Utilisateur supprimé');
      fetchAll();
    } catch (err) { showToast(err.response?.data?.detail || 'Erreur', 'error'); }
  };

  const handleDeleteMessage = async (id) => {
    await axios.delete(`${API}/admin/messages/${id}`);
    fetchAll();
  };

  const handleMarkRead = async (id) => {
    await axios.patch(`${API}/admin/messages/${id}/read`);
    fetchAll();
  };

  const handleSendNotif = async (e) => {
    e.preventDefault();
    setSendingNotif(true);
    try {
      await axios.post(`${API}/admin/notifications`, newNotif);
      setNewNotif({ title: '', message: '' });
      showToast('Notification envoyée à tous les clients');
      fetchAll();
    } catch (err) { showToast(err.response?.data?.detail || 'Erreur', 'error'); }
    finally { setSendingNotif(false); }
  };

  const handleDeleteNotif = async (id) => {
    await axios.delete(`${API}/admin/notifications/${id}`);
    fetchAll();
  };

  const unreadCount = messages.filter(m => !m.read).length;
  const clientCount = users.filter(u => u.role !== 'admin').length;

  const tabs = [
    { id: 'audio', label: 'Audios', icon: Headphones },
    { id: 'tips', label: 'Conseils', icon: FileText },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'users', label: 'Clients', icon: Users },
    { id: 'messages', label: `Messages${unreadCount > 0 ? ` (${unreadCount})` : ''}`, icon: Mail },
  ];

  const inputCls = "w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 text-sm";

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-dashboard">
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-3xl font-semibold text-[#2C3531] mb-4" style={{ fontFamily: 'Outfit' }}>Administration</motion.h1>

        {/* Compteur clients */}
        <div className="mb-8 inline-flex items-center gap-3 bg-white border border-[#EBE7E0] rounded-2xl px-5 py-3" data-testid="admin-client-count">
          <Users className="w-5 h-5 text-[#6B8576]" strokeWidth={1.5} />
          <span className="text-sm text-[#2C3531]"><span className="font-semibold text-lg text-[#6B8576]">{clientCount}</span> client{clientCount > 1 ? 's' : ''} inscrit{clientCount > 1 ? 's' : ''}</span>
        </div>

        {toast && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`mb-6 p-3.5 rounded-2xl text-sm font-medium ${toast.type === 'success' ? 'bg-green-50 text-[#6B8576] border border-green-100' : toast.type === 'info' ? 'bg-amber-50 text-amber-700 border border-amber-100' : 'bg-red-50 text-[#B86666] border border-red-100'}`}>
            {toast.msg}
          </motion.div>
        )}

        <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-2 transition-all duration-300 whitespace-nowrap ${tab === t.id ? 'bg-[#6B8576] text-white shadow-sm' : 'bg-white text-[#2C3531] border border-[#EBE7E0] hover:bg-[#FAF9F6]'}`} data-testid={`admin-tab-${t.id}`}>
              <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>

        {/* AUDIO TAB */}
        {tab === 'audio' && (
          <div className="space-y-6">
            <form onSubmit={handleUploadAudio} className="bg-white rounded-3xl border border-[#EBE7E0] p-6 space-y-4" data-testid="upload-audio-form">
              <h3 className="font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Uploader un audio</h3>
              <input type="text" placeholder="Titre" className={inputCls} value={uploadForm.title} onChange={e => setUploadForm({...uploadForm, title: e.target.value})} required data-testid="upload-title" />
              <input type="text" placeholder="Description (optionnel)" className={inputCls} value={uploadForm.description} onChange={e => setUploadForm({...uploadForm, description: e.target.value})} data-testid="upload-description" />
              <input type="file" accept=".mp3,.wav,.m4a" ref={fileRef} className="w-full text-sm text-[#6A7771]" required data-testid="upload-file" />
              <button type="submit" disabled={uploading} className="px-6 py-2.5 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-50" data-testid="upload-submit">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Uploader'}
              </button>
            </form>

            <div className="space-y-4">
              {audios.map(a => {
                const grantedUserIds = audioAccess[a.id] || [];
                const clients = users.filter(u => u.role !== 'admin');
                const grantedCount = clients.filter(c => grantedUserIds.includes(c.id)).length;
                const isPrivate = a.private === true;
                return (
                <div key={a.id} className="bg-white rounded-3xl border border-[#EBE7E0] p-6" data-testid={`admin-audio-${a.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#6B8576]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Headphones className="w-5 h-5 text-[#6B8576]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h4 className="font-medium text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>{a.title}</h4>
                        {a.description && <p className="text-sm text-[#6A7771] mt-0.5">{a.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => handleTogglePrivate(a.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-all duration-300 ${isPrivate ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-[#6B8576]/10 text-[#6B8576] border border-[#6B8576]/20'}`} data-testid={`toggle-private-${a.id}`}>
                        {isPrivate ? <><Lock className="w-3 h-3" strokeWidth={2} /> Privé</> : <><Globe className="w-3 h-3" strokeWidth={2} /> Public</>}
                      </button>
                      <button onClick={() => handleDeleteAudio(a.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors duration-200"><Trash2 className="w-4 h-4 text-[#B86666]" strokeWidth={1.5} /></button>
                    </div>
                  </div>

                  {/* Grant access section - only for private audios */}
                  {isPrivate && (
                  <div className="bg-[#FAF9F6] rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-[#2C3531]">
                        <UserPlus className="w-4 h-4 inline mr-1.5 text-[#6B8576]" strokeWidth={1.5} />
                        Débloquer pour les clients {clients.length > 0 && <span className="text-[#6A7771] font-normal">({grantedCount}/{clients.length})</span>}
                      </p>
                      <button onClick={() => handleGrantAll(a.id)} className="px-4 py-1.5 bg-[#6B8576] text-white rounded-full text-xs font-medium hover:bg-[#546A5E] transition-all duration-300" data-testid={`grant-all-${a.id}`}>Débloquer pour tous</button>
                    </div>
                    {clients.length === 0 ? (
                      <p className="text-sm text-[#6A7771] italic">Aucun client inscrit pour le moment</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {clients.map(u => {
                          const hasAccess = grantedUserIds.includes(u.id);
                          return (
                            <button key={u.id} onClick={() => { if (!hasAccess) { handleGrantUser(a.id, u.id); setTimeout(fetchAll, 500); } }}
                              className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-all duration-300 ${hasAccess ? 'bg-[#6B8576]/10 text-[#6B8576] border border-[#6B8576]/20 cursor-default' : 'bg-white border border-[#EBE7E0] text-[#2C3531] hover:bg-[#E4D1C3]/30 hover:border-[#D9A892]/40 cursor-pointer'}`}
                              data-testid={`grant-${a.id}-${u.id}`}
                            >
                              {hasAccess && <Check className="w-3 h-3" strokeWidth={2.5} />}
                              {u.name || u.email}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  )}
                  {!isPrivate && (
                    <p className="text-sm text-[#6B8576] italic flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" strokeWidth={1.5} /> Accessible à tous les clients</p>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* TIPS TAB */}
        {tab === 'tips' && (
          <div className="space-y-6">
            <form onSubmit={handleCreateTip} className="bg-white rounded-3xl border border-[#EBE7E0] p-6 space-y-4" data-testid="create-tip-form">
              <h3 className="font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Publier un conseil</h3>
              <input type="text" placeholder="Titre" className={inputCls} value={newTip.title} onChange={e => setNewTip({...newTip, title: e.target.value})} required data-testid="tip-title" />
              <textarea placeholder="Contenu du conseil..." className={`${inputCls} min-h-[100px]`} value={newTip.content} onChange={e => setNewTip({...newTip, content: e.target.value})} required data-testid="tip-content" />
              <button type="submit" className="px-6 py-2.5 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300" data-testid="tip-submit">Publier</button>
            </form>
            <div className="space-y-3">
              {tips.map(t => (
                <div key={t.id} className="bg-white rounded-3xl border border-[#EBE7E0] p-5 flex justify-between items-start gap-3" data-testid={`admin-tip-${t.id}`}>
                  <div><h4 className="font-medium text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>{t.title}</h4><p className="text-sm text-[#6A7771] mt-1 leading-relaxed">{t.content}</p></div>
                  <button onClick={() => handleDeleteTip(t.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors duration-200 flex-shrink-0"><Trash2 className="w-4 h-4 text-[#B86666]" strokeWidth={1.5} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOTIFICATIONS TAB */}
        {tab === 'notifications' && (
          <div className="space-y-6">
            <form onSubmit={handleSendNotif} className="bg-white rounded-3xl border border-[#EBE7E0] p-6 space-y-4" data-testid="send-notif-form">
              <h3 className="font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Envoyer une notification</h3>
              <p className="text-sm text-[#6A7771]">La notification sera visible par tous les clients dans leur cloche de notifications.</p>
              <input type="text" placeholder="Titre (ex: Nouvel outil disponible)" className={inputCls} value={newNotif.title} onChange={e => setNewNotif({...newNotif, title: e.target.value})} required data-testid="notif-title" />
              <textarea placeholder="Message..." className={`${inputCls} min-h-[100px]`} value={newNotif.message} onChange={e => setNewNotif({...newNotif, message: e.target.value})} required data-testid="notif-message" />
              <button type="submit" disabled={sendingNotif} className="px-6 py-2.5 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-50 flex items-center gap-2" data-testid="notif-submit">
                {sendingNotif ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />} Envoyer
              </button>
            </form>
            <div className="space-y-3">
              <h3 className="font-semibold text-[#2C3531] text-sm" style={{ fontFamily: 'Outfit' }}>Historique des notifications</h3>
              {adminNotifs.length === 0 ? (
                <div className="text-center py-12 text-[#6A7771]">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" strokeWidth={1.5} />
                  <p className="font-medium">Aucune notification envoyée</p>
                </div>
              ) : adminNotifs.map(n => (
                <div key={n.id} className="bg-white rounded-3xl border border-[#EBE7E0] p-5 flex justify-between items-start gap-3" data-testid={`admin-notif-${n.id}`}>
                  <div>
                    <h4 className="font-medium text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>{n.title}</h4>
                    <p className="text-sm text-[#6A7771] mt-1 leading-relaxed">{n.message}</p>
                    <p className="text-xs text-[#6A7771] mt-2">{new Date(n.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <button onClick={() => handleDeleteNotif(n.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors duration-200 flex-shrink-0"><Trash2 className="w-4 h-4 text-[#B86666]" strokeWidth={1.5} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {tab === 'users' && (
          <div className="space-y-3">
            {users.map(u => (
              <div key={u.id} className="bg-white rounded-3xl border border-[#EBE7E0] p-5" data-testid={`admin-user-${u.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>{u.name || 'Sans nom'}</h4>
                    <p className="text-sm text-[#6A7771]">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-[#6B8576]/10 text-[#6B8576]' : 'bg-[#E4D1C3]/30 text-[#C2937E]'}`}>{u.role === 'admin' ? 'admin' : 'client'}</span>
                      <p className="text-xs text-[#6A7771] mt-1">{u.audio_count} audios · {u.exercise_count} exercices</p>
                    </div>
                    {u.role !== 'admin' && (
                      <button onClick={() => handleDeleteUser(u.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors duration-200"><Trash2 className="w-4 h-4 text-[#B86666]" strokeWidth={1.5} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MESSAGES TAB */}
        {tab === 'messages' && (
          <div className="space-y-3">
            {messages.length === 0 ? (
              <div className="text-center py-16 text-[#6A7771]">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" strokeWidth={1.5} />
                <p className="font-medium">Aucun message</p>
              </div>
            ) : messages.map(m => (
              <div key={m.id} className={`bg-white rounded-3xl border p-5 transition-all duration-200 ${m.read ? 'border-[#EBE7E0]' : 'border-[#6B8576]/30 shadow-[0_0_0_1px_rgba(107,133,118,0.1)]'}`} data-testid={`admin-msg-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {!m.read && <span className="w-2 h-2 bg-[#6B8576] rounded-full flex-shrink-0" />}
                      <h4 className="font-medium text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>{m.name}</h4>
                      <span className="text-xs text-[#6A7771]">{new Date(m.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <a href={`mailto:${m.email}`} className="text-sm text-[#6B8576] hover:underline">{m.email}</a>
                    <p className="text-sm text-[#2C3531] mt-2 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <a href={`mailto:${m.email}?subject=Re: Votre message sur Audio Sur Mesure&body=%0A%0A---%0AVotre message :%0A${encodeURIComponent(m.message)}`} className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#6B8576] text-white rounded-full text-xs font-medium hover:bg-[#546A5E] transition-all duration-300" data-testid={`reply-msg-${m.id}`}>
                        <Reply className="w-3.5 h-3.5" strokeWidth={1.5} /> Répondre
                      </a>
                      {!m.read && (
                        <button onClick={() => handleMarkRead(m.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FAF9F6] border border-[#EBE7E0] text-[#6A7771] rounded-full text-xs font-medium hover:bg-[#EBE7E0]/50 transition-all duration-300" data-testid={`mark-read-${m.id}`}>
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Marquer comme lu
                        </button>
                      )}
                    </div>
                  </div>
                  <button onClick={() => handleDeleteMessage(m.id)} className="p-2 hover:bg-red-50 rounded-xl transition-colors duration-200 flex-shrink-0"><Trash2 className="w-4 h-4 text-[#B86666]" strokeWidth={1.5} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
